import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  fetchSchemas,
  fetchSchemaById,
  createSchema,
  updateSchema,
  deleteSchema,
  publishSchema,
  cloneSchema,
  deprecateSchema,
} from './schemasThunks';

import type { RootState } from '@/store/root-state';
import type { JsonSchema, SchemaRecord, SchemaStatus } from '@/domain/entities/property-schema';

type StatusMap<T> = Record<SchemaStatus, T>;

export interface SchemasState {
  // ✅ status-based caches
  itemsByStatus: StatusMap<SchemaRecord[]>;
  hasFetchedByStatus: StatusMap<boolean>;
  isListingByStatus: StatusMap<boolean>;
  errorByStatus: StatusMap<string | null>;

  // ✅ UI filter
  activeStatus: SchemaStatus;

  // ✅ editor
  selected: SchemaRecord | null;

  isLoadingOne: boolean;
  isSaving: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isPublishing: boolean;
  isCloning: boolean;

  hasChanges: boolean;
}

const initialState: SchemasState = {
  itemsByStatus: { draft: [], published: [], deprecated: [] },
  hasFetchedByStatus: { draft: false, published: false, deprecated: false },
  isListingByStatus: { draft: false, published: false, deprecated: false },
  errorByStatus: { draft: null, published: null, deprecated: null },

  activeStatus: 'draft',
  selected: null,

  isLoadingOne: false,
  isSaving: false,
  isUploading: false,
  isDeleting: false,
  isPublishing: false,
  isCloning: false,

  hasChanges: false,
};

const samePkSk = (a: Pick<SchemaRecord, 'id' | 'version'>, b: Pick<SchemaRecord, 'id' | 'version'>) =>
  a.id === b.id && a.version === b.version;

const upsertIntoBucket = (items: SchemaRecord[], rec: SchemaRecord): SchemaRecord[] => {
  const idx = items.findIndex((x) => samePkSk(x, rec));
  if (idx === -1) return [rec, ...items];
  const copy = items.slice();
  copy[idx] = rec;
  return copy;
};

const removeFromBucket = (items: SchemaRecord[], id: string, version: string): SchemaRecord[] =>
  items.filter((x) => !(x.id === id && x.version === version));

export const schemasSlice = createSlice({
  name: 'propertySchemas',
  initialState,
  reducers: {
    setActiveStatus: (state, action: PayloadAction<SchemaStatus>) => {
      state.activeStatus = action.payload;
    },

    clearSelectedSchema: (state) => {
      state.selected = null;
      state.hasChanges = false;
    },

    patchSelectedMeta: (
      state,
      action: PayloadAction<Pick<SchemaRecord, 'title' | 'description'> & { status?: SchemaRecord['status'] }>,
    ) => {
      if (!state.selected) return;

      const nextStatus = action.payload.status ?? state.selected.status;

      // If status changes locally, move it between buckets immediately
      if (nextStatus !== state.selected.status) {
        const id = state.selected.id;
        const version = state.selected.version;
        state.itemsByStatus[state.selected.status] = removeFromBucket(
          state.itemsByStatus[state.selected.status],
          id,
          version,
        );

        const moved: SchemaRecord = {
          ...state.selected,
          title: action.payload.title,
          description: action.payload.description,
          status: nextStatus,
        };
        state.itemsByStatus[nextStatus] = upsertIntoBucket(state.itemsByStatus[nextStatus], moved);
        state.selected = moved;
      } else {
        const updated: SchemaRecord = {
          ...state.selected,
          title: action.payload.title,
          description: action.payload.description,
          status: nextStatus,
        };
        state.itemsByStatus[nextStatus] = upsertIntoBucket(state.itemsByStatus[nextStatus], updated);
        state.selected = updated;
      }

      state.hasChanges = true;
    },

    setSelectedJsonSchema: (state, action: PayloadAction<JsonSchema>) => {
      if (!state.selected) return;

      const updated: SchemaRecord = { ...state.selected, jsonSchema: action.payload };
      state.itemsByStatus[updated.status] = upsertIntoBucket(state.itemsByStatus[updated.status], updated);
      state.selected = updated;
      state.hasChanges = true;
    },

    markSchemasClean: (state) => {
      state.hasChanges = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // list (bucket results)
      // list (FIX: write to the bucket that was requested, not activeStatus)
      .addCase(fetchSchemas.pending, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = true;
        state.errorByStatus[status] = null;
      })
      .addCase(fetchSchemas.fulfilled, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = false;
        state.itemsByStatus[status] = action.payload.schemas;
        state.hasFetchedByStatus[status] = true;
      })
      .addCase(fetchSchemas.rejected, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = false;
        state.hasFetchedByStatus[status] = true;
        state.errorByStatus[status] =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to fetch schemas';
      })

      // get by id
      .addCase(fetchSchemaById.pending, (state) => {
        state.isLoadingOne = true;
      })
      .addCase(fetchSchemaById.fulfilled, (state, action) => {
        state.isLoadingOne = false;

        const rec = action.payload as SchemaRecord;
        state.selected = rec;

        // Upsert into correct bucket
        state.itemsByStatus[rec.status] = upsertIntoBucket(state.itemsByStatus[rec.status], rec);
        state.hasChanges = false;
      })
      .addCase(fetchSchemaById.rejected, (state) => {
        state.isLoadingOne = false;
      })

      // create
      .addCase(createSchema.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createSchema.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload.data as SchemaRecord;
        state.selected = rec;

        state.itemsByStatus[rec.status] = upsertIntoBucket(state.itemsByStatus[rec.status], rec);
        state.hasChanges = false;
      })
      .addCase(createSchema.rejected, (state) => {
        state.isSaving = false;
      })

      // update
      .addCase(updateSchema.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateSchema.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload.data as SchemaRecord;
        state.selected = rec;

        // remove from all buckets then insert into correct bucket (handles status changes)
        (['draft', 'published', 'deprecated'] as SchemaStatus[]).forEach((s) => {
          state.itemsByStatus[s] = removeFromBucket(state.itemsByStatus[s], rec.id, rec.version);
        });
        state.itemsByStatus[rec.status] = upsertIntoBucket(state.itemsByStatus[rec.status], rec);

        state.hasChanges = false;
      })
      .addCase(updateSchema.rejected, (state) => {
        state.isSaving = false;
      })
      // delete (HEAD draft only)
      .addCase(deleteSchema.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteSchema.fulfilled, (state, action) => {
        state.isDeleting = false;

        // Prefer id from arg (works even if API returns nothing)
        const id = action.meta.arg.id;

        // Remove from all buckets (safe)
        (['draft', 'published', 'deprecated'] as SchemaStatus[]).forEach((s) => {
          state.itemsByStatus[s] = removeFromBucket(state.itemsByStatus[s], id, 'HEAD');
        });

        // Clear selection if it was the deleted one
        if (state.selected && state.selected.id === id && state.selected.version === 'HEAD') {
          state.selected = null;
          state.hasChanges = false;
        }
      })
      .addCase(deleteSchema.rejected, (state, action) => {
        state.isDeleting = false;

        // Optional: you can store this somewhere; simplest is to put it on active tab error
        const status = state.activeStatus;
        state.errorByStatus[status] =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to delete schema';
      })
      // publish (creates immutable VER#n snapshot)
      .addCase(publishSchema.pending, (state) => {
        state.isPublishing = true;
        state.errorByStatus.draft = null;
      })
      .addCase(publishSchema.fulfilled, (state) => {
        state.isPublishing = false;

        // We don’t get the new VER#n in response (void),
        // so mark published cache stale to force refetch.
        state.hasFetchedByStatus.published = false;

        // Draft HEAD remains draft; keep draft cache as-is.
        // Optional: if you want to immediately reflect changes by refetching from UI, you already do that.
      })
      .addCase(publishSchema.rejected, (state, action) => {
        state.isPublishing = false;

        // Publish is initiated from draft tab; store error there
        state.errorByStatus.draft =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to publish schema';
      })
      // clone (creates new HEAD draft from a published snapshot)
      .addCase(cloneSchema.pending, (state) => {
        state.isCloning = true;
        state.errorByStatus.published = null;
      })
      .addCase(cloneSchema.fulfilled, (state, action) => {
        state.isCloning = false;

        // backend returns the newly created HEAD draft record (recommended)
        const rec = action.payload.data as SchemaRecord;

        state.selected = rec;

        // Put new HEAD draft into draft bucket
        state.itemsByStatus.draft = upsertIntoBucket(state.itemsByStatus.draft, rec);

        // Draft list changed -> mark draft fetched (optional: keep true)
        state.hasFetchedByStatus.draft = false;

        // Optionally: published list unchanged
        state.hasChanges = false;
      })
      .addCase(cloneSchema.rejected, (state, action) => {
        state.isCloning = false;

        // Clone is initiated from published tab; store error there
        state.errorByStatus.published =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to clone schema';
      })
      .addCase(deprecateSchema.pending, (state) => {
        state.isPublishing = true; // reuse flag or add isDeprecating if you prefer
      })
      .addCase(deprecateSchema.fulfilled, (state) => {
        state.isPublishing = false;

        // published list is now stale
        state.hasFetchedByStatus.published = false;
        state.hasFetchedByStatus.deprecated = false;
      })
      .addCase(deprecateSchema.rejected, (state, action) => {
        state.isPublishing = false;
        state.errorByStatus.published =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to deprecate schema';
      });
  },
});

export const { setActiveStatus, clearSelectedSchema, patchSelectedMeta, setSelectedJsonSchema, markSchemasClean } =
  schemasSlice.actions;

// ----------------- selectors -----------------
const selectSchemasState = (state: RootState) => state.propertySchemas;

export const selectSchemasForActiveStatus = createSelector(
  [selectSchemasState],
  (s) => s.itemsByStatus[s.activeStatus],
);

export const selectPublishedSchemas = createSelector([selectSchemasState], (s) =>
  [...s.itemsByStatus.published].sort((a, b) => a.title.localeCompare(b.title)),
);

export const selectPublishedSchemaOptions = createSelector([selectPublishedSchemas], (items) =>
  items.map((x) => ({
    label: `${x.title} (${x.id} / ${x.version})`,
    value: `${x.id}::${x.version}`,
  })),
);

export const selectSchemaByPkSk = createSelector(
  [
    (state: RootState) => state.propertySchemas,
    (_: RootState, id?: string | null) => id,
    (_: RootState, _id?: string | null, version?: string | null) => version ?? 'HEAD',
  ],
  (schemas, id, version) => {
    if (!id) return null;

    // Prefer selected if it matches exactly (freshest)
    if (schemas.selected && schemas.selected.id === id && schemas.selected.version === version) {
      return schemas.selected;
    }

    // Search all buckets
    const buckets = schemas.itemsByStatus;
    const all = [...buckets.draft, ...buckets.published, ...buckets.deprecated];

    return all.find((s) => s.id === id && s.version === version) ?? null;
  },
);

export default schemasSlice.reducer;
